import React, { useEffect, useMemo, useState } from 'react';

import { AxiosResponse } from 'axios';

import { useDebounce } from '@shared/hooks';

import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks';
import { useTitle } from '@admin/hooks/use_title';

import { attachCustomers, customersRequest, extractErrorMessage, transferablesRequest } from './api';
import { RequestTransferModal, TransferablesPalletGrid } from './pallets';
import { TransferablesRegionSelect } from './region_select';
import {
  buildDateConstraints,
  formatFilterDateValue,
  parseDateInput,
  parseFiltersFromSearch,
  syncFiltersToUrl,
} from './transferables_filters';
import { PALLET_STATES, Transferable, TransferablesCustomer, TransferablesFilters, TransferablesPallet } from './types';
import { TransferablesWarehouseSelect } from './warehouse_select';

export const TransferablesIndex: React.FC = () => {
  useTitle('Transferables');
  const [filters, setFilters] = useState<TransferablesFilters>(() => parseFiltersFromSearch(window.location.search));
  const [selectedPalletIDs, setSelectedPalletIDs] = useState<Set<number>>(new Set());
  const [schedulingTransferable, setSchedulingTransferable] = useState<Transferable | undefined>();
  const [listRequest, setListRequest] = useState<Promise<AxiosResponse<Transferable[]>> | undefined>();
  const [customersReq, setCustomersReq] = useState<Promise<AxiosResponse<TransferablesCustomer[]>> | undefined>();

  const debouncedFilters = useDebounce(filters);
  const dateConstraints = useMemo(() => buildDateConstraints(filters), [filters.from, filters.till]);

  useEffect(() => {
    setSelectedPalletIDs(new Set());
    syncFiltersToUrl(debouncedFilters);
    setListRequest(transferablesRequest(debouncedFilters));
    setCustomersReq(undefined);
  }, [debouncedFilters]);

  const { loading: listLoading, response: listResponse, error: listError } = useAxiosRequest(listRequest);

  useEffect(() => {
    if (!listResponse) {
      return;
    }
    setCustomersReq(customersRequest(listResponse.data));
  }, [listResponse]);

  const {
    loading: customersLoading,
    response: customersResponse,
    error: customersError,
  } = useAxiosRequest(customersReq);

  const transferables = useMemo(() => {
    if (!listResponse) {
      return undefined;
    }
    const accountIds = listResponse.data.flatMap((transferable) =>
      transferable.pallets.flatMap((pallet) => pallet.account_ids),
    );
    if (accountIds.length === 0) {
      return attachCustomers(listResponse.data, []);
    }
    if (!customersResponse) {
      return undefined;
    }
    return attachCustomers(listResponse.data, customersResponse.data);
  }, [customersResponse, listResponse]);

  const loadError = listError || customersError ? extractErrorMessage(listError ?? customersError) : undefined;
  const loading = (!!listLoading || (!!listResponse && customersReq !== undefined && !!customersLoading)) && !loadError;

  const updateFilters = (next: Partial<TransferablesFilters>) => {
    setFilters((current) => ({ ...current, ...next }));
  };

  const togglePalletSelection = (pallet: TransferablesPallet, checked: boolean) => {
    setSelectedPalletIDs((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(pallet.id);
      } else {
        next.delete(pallet.id);
      }
      return next;
    });
  };

  const schedulablePallets = (transferable: Transferable) =>
    transferable.pallets.filter((pallet) => selectedPalletIDs.has(pallet.id));

  const reloadTransferables = () => {
    setSelectedPalletIDs(new Set());
    syncFiltersToUrl(filters);
    setListRequest(transferablesRequest(filters));
    setCustomersReq(undefined);
  };

  return (
    <>
      {schedulingTransferable && (
        <RequestTransferModal
          transferable={schedulingTransferable}
          pallets={schedulablePallets(schedulingTransferable)}
          onClose={() => setSchedulingTransferable(undefined)}
          onSuccess={reloadTransferables}
        />
      )}

      <Panel>
        <Panel.Body>
          <form className="form-inline">
            <span className="form-grouping">
              <label htmlFor="transferables-region">Region:</label>
              <TransferablesRegionSelect
                id="transferables-region"
                value={filters.region_id}
                onChange={(region_id) => updateFilters({ region_id })}
              />
            </span>

            <span className="form-grouping">
              <label htmlFor="transferables-warehouse">Warehouse:</label>
              <TransferablesWarehouseSelect
                id="transferables-warehouse"
                regionID={filters.region_id}
                value={filters.warehouse_id}
                onChange={(warehouse_id) => updateFilters({ warehouse_id })}
              />
            </span>

            <span className="form-grouping">
              <label htmlFor="transferables-state">State:</label>
              <select
                id="transferables-state"
                className="form-control"
                value={filters.state ?? ''}
                onChange={(event) =>
                  updateFilters({ state: (event.target.value || undefined) as TransferablesFilters['state'] })
                }
              >
                <option value=""> - State - </option>
                {PALLET_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase())}
                  </option>
                ))}
              </select>
            </span>

            <span className="form-grouping">
              <label htmlFor="transferables-from">Dates:</label>
              <input
                id="transferables-from"
                type="date"
                className="form-control"
                value={formatFilterDateValue(filters.from)}
                min={formatFilterDateValue(dateConstraints.from.min)}
                max={formatFilterDateValue(dateConstraints.from.max)}
                onChange={(event) => {
                  const parsed = parseDateInput(event.target.value);
                  if (parsed) {
                    updateFilters({ from: parsed });
                  }
                }}
              />
              <input
                id="transferables-till"
                type="date"
                className="form-control"
                aria-label="End date"
                value={formatFilterDateValue(filters.till)}
                min={formatFilterDateValue(dateConstraints.till.min)}
                max={formatFilterDateValue(dateConstraints.till.max)}
                onChange={(event) => {
                  const parsed = parseDateInput(event.target.value);
                  if (parsed) {
                    updateFilters({ till: parsed });
                  }
                }}
              />
            </span>
          </form>
        </Panel.Body>
      </Panel>

      {loading && <Spinner />}

      {loadError && (
        <div className="alert alert-danger" role="alert">
          Failed to load transferables: {loadError}
        </div>
      )}

      {transferables && transferables.length === 0 && !loadError && (
        <div className="well text-center">No Matches Found!</div>
      )}

      {transferables?.map((transferable) => (
        <TransferablesPalletGrid
          key={transferable.warehouse.id}
          transferable={transferable}
          selectedPalletIDs={selectedPalletIDs}
          onTogglePallet={togglePalletSelection}
          onRequestTransfer={() => setSchedulingTransferable(transferable)}
        />
      ))}
    </>
  );
};
